import { EventEmitterService } from '@core/services/event-emitter.service';
import { SubheaderService } from '@core/_base/layout/services/subheader.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { Pagination } from '@core/models/pagination.model';
import { map, exhaustMap, tap } from 'rxjs/operators';
import { FormGroup, FormControl } from '@angular/forms';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { BotStatusDataService } from './service/bot-status-data.service';
import { BotStatusEntityService } from '@views/pages/apps/settings/bot-status/service/bot-status-entity.service';
import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { BotStatus } from '@core/models/bot-status.model';
import { MerchantBankHttpService } from '@core/services/merchant-bank-http.service';
import { AppPermissionService } from '@core/services/app-permission.service';
import * as moment from 'moment-timezone';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { ActivatedRoute } from '@angular/router';
import { ReconciliationJobCreateComponent } from './dialogs/reconciliation-job-create.component';
import { BotTransactionListComponent } from './dialogs/bot-transaction-list/bot-transaction-list.component';
import { MatDialog } from '@angular/material/dialog';
import { GameProviderPositionEditDialogComponent } from '../../games/game-provider-position/dialogs/game-provider-position-edit.component';

@Component({
  selector: 'kt-bot-status',
  templateUrl: './bot-status.component.html',
  styleUrls: ['./bot-status.component.scss']
})
export class BotStatusComponent implements OnInit, OnDestroy {
  form: FormGroup;
  dropdown = {
    perPage: this.dropdownHttpService.perPage,
    servers: [],
    stages: []
  };
  botStatus$: Observable<BotStatus[]>;
  statuses = [
    "Disabled",
    "Enabled (Auto)",
    "Enabled (Manual)"
  ];

  modes = [
    "NULL",
    "Deposit",
    "Withdrawal",
    "Auto"
  ];

  bankaccountDropdownSettings = {};
  bankaccountDropdownList = [];
  bankaccountSelectedItems: any;

  bankserverDropdownSettings = {};
  bankserverDropdownList = [];
  bankserverSelectedItems: any;

  bankstageDropdownSettings = {};
  bankstageDropdownList = [];
  bankstageSelectedItems: any;

  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';

  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  dataLength = 0;

  messages$ = this.botStatusDataService.messages$;

  // permissions
  canEnterTAC: boolean;
  canUpdateBankBotStatus: boolean;
  canUpdateBankBotServer: boolean;
  canUpdateBankBotMode: boolean;
  canCreateReconciliationJob: boolean;

  private subscription = new Subscription();
  private subscriptions = new Subscription();

  dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  ranges: any;

  dateTimeFilterType = [
    {
      key: 'last_run_at',
      label: 'Last Run Date'
    },
    {
      key: 'created_at',
      label: 'Created Date'
    },
    {
      key: 'updated_at',
      label: 'Updated Date'
    }
  ];

  constructor(
    private botStatusEntityService: BotStatusEntityService,
    private botStatusDataService: BotStatusDataService,
    private dropdownHttpService: DropdownHttpService,
    private merchantBankHttpService: MerchantBankHttpService,
    private loadingBar: LoadingBarService,
    private subheaderService: SubheaderService,
    private eventEmitterService: EventEmitterService,
    private appPermissionService: AppPermissionService,
    private transactionHttpService: TransactionHttpService,
    private route: ActivatedRoute,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.botStatusDataService.getBankBotServer().subscribe(res => {
      const manualRow = { id: 0, hostname: 'No Server' };
      this.dropdown.servers = [manualRow, ...res];
      this.bankserverDropdownList = [manualRow, ...res];
    })

    this.botStatusDataService.getBankBotStage().subscribe(res => {
      this.dropdown.stages = res;
      this.bankstageDropdownList = res;
    })

    this.reload();
    this.formInit();
    this.pagination = this.botStatusDataService.pagination;

    // for bank bot tac notification usage (get the banksys id from route param)
    this.route.queryParams.subscribe(params => {
      const banksysId = params['banksys-id'];
      if (banksysId != undefined) {
        this.form.patchValue({
          ...this.form,
          banksys_id: banksysId
        });
        this.onSubmit(true);
      }
    });

    this.bankaccountDropdownSettings = {
      singleSelection: true,
      text: 'Please Select',
      enableFilterSelectAll: false,
      enableSearchFilter: true,
      classes: 'dropdown',
      // maxHeight: 'auto',
      primaryKey: 'name',
      labelKey: 'name',
      noDataLabel: '',
      showCheckbox: false
    };

    this.bankserverDropdownSettings = {
      singleSelection: true,
      text: 'Please Select',
      enableFilterSelectAll: false,
      enableSearchFilter: true,
      classes: 'dropdown',
      // maxHeight: 'auto',
      primaryKey: 'hostname',
      labelKey: 'hostname',
      noDataLabel: '',
      showCheckbox: false
    };

    this.bankstageDropdownSettings = {
      singleSelection: false,
      text: 'Please Select',
      enableFilterSelectAll: true,
      enableSearchFilter: true,
      classes: 'dropdown',
      // maxHeight: 'auto',
      primaryKey: 'stage_name',
      labelKey: 'stage_name',
      noDataLabel: '',
      showCheckbox: false
    };

    this.merchantBankHttpService.getMerchantBankAccounts('&sync=1').subscribe(
      res => {
        res.map(elm => {
          elm.name = elm.bank_bcode + ' - ' + elm.account_name + ' - ' + elm.account_number;
        });
        this.bankaccountDropdownList = res;
        this.bankaccountDropdownList.unshift({
          id: 'all',
          name: 'All'
        });
        this.bankaccountSelectedItems = [this.bankaccountDropdownList.find(v => v.id === 'all')];
      }
    );

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canEnterTAC = appPermissions.enter_bank_bot_tac;
      this.canUpdateBankBotStatus = appPermissions.update_bank_bot_status;
      this.canUpdateBankBotServer = appPermissions.update_bank_bot_server;
      this.canUpdateBankBotMode = appPermissions.update_bank_bot_mode;
      this.canCreateReconciliationJob = appPermissions.create_reconciliation_job;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    return this.botStatus$ = this.botStatusEntityService.getWithQuery(`?page=${page}&perPage=${pageSize}${params}`).pipe(
      tap(res => {
        this.loading = false;
        this.dataLength = res.length;
        this.pagination = this.botStatusDataService.pagination;
        this.loadingBar.complete();
      })
    );
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onCloseDialog() {
  }

  onClear() {
    this.bankaccountSelectedItems = [this.bankaccountDropdownList.find(v => v.id === 'all')];
    this.clearBtnLoading = true;
    this.eventEmitterService.onClearMerchantAccountSearch();
    this.form.patchValue({
      merchant_bank_id: 'all',
      start_date: null,
      end_date: null,
      defaultDate: null,
      date_type: this.dateTimeFilterType[0].key,
      banksys_id: null,
      bank_bot_server_id: null,
      stage: [],
    });
    this.onSubmit(true);
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = clearSearch ? false : true;
    this.loading = true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        // add 8 hour, bant bot +8 timezone
        if (data['start_date']) {
          data['start_date'] = moment(data['start_date']).add(8, 'hours').tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_date'] = moment(data['end_date']).add(8, 'hours').tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }
        if(data['defaultDate']){
          delete data['defaultDate'];
        }
        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        const parameters = this.params ? `&${this.params}` : '';
        this.loadingBar.start();
        return this.botStatus$ = this.botStatusEntityService.getWithQuery(`?perPage=${this.pageSize}${parameters}`).pipe(
          tap(res => {
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.dataLength = res.length;
            this.page = 1;
            this.pagination = this.botStatusDataService.pagination;
            this.loadingBar.complete();
          })
        );
      }),
    ).subscribe();
  }

  private formInit() {
    this.form = new FormGroup({
      merchant_bank_id: new FormControl('all'),
      date_type: new FormControl(this.dateTimeFilterType[0].key),
      start_date: new FormControl(null),
      end_date: new FormControl(null),
      defaultDate: new FormControl(null),
      banksys_id: new FormControl(null),
      bank_bot_server_id: new FormControl(null),
      stage: new FormControl([]),
    });
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => (formData[key] !== '' && formData[key] !== null && key !== 'defaultDate') ? fields[key] = formData[key] : key);
    return fields;
  }

  reload() {
    this.onViewPageBy(this.page).subscribe();
  }

  updateSwitch(row: any, value: number) {
    if (row.switch !== value) {
      const data = {
        switch: value,
        mode: row.mode,
        account_number: row.account_number
      };
      this.subscription = this.botStatusDataService.updateSwitch(row.bank_account_id, data).subscribe(() =>
        this.messages$ = this.botStatusDataService.messages$
      );
    }
  }

  updateBotServer(row: any, value: number) {
    if (row.bank_bot_server_id !== value) {
      const data = {
        bank_bot_server_id: value
      };
      this.subscription = this.botStatusDataService.updateSwitch(row.bank_account_id, data).subscribe(() =>
        this.messages$ = this.botStatusDataService.messages$
      );
    }
  }

  updateBotMode(row: any, value: number) {
    if (row.bank_bot_server_id !== value) {
      const data = {
        mode: value
      };
      this.subscription = this.botStatusDataService.updateSwitch(row.bank_account_id, data).subscribe(() =>
        this.messages$ = this.botStatusDataService.messages$
      );
    }
  }

  updateTAC(row: any, requesterRole: number, action: number) {
    const isChecker = requesterRole === 1;
    const username = isChecker ? row.sub_username : row.username;
    const challengeCode = isChecker ? row.checker_tac_auth_reference : row.tac_auth_reference;
    const title = row.sub_account ? (isChecker ? 'Enter Checker TAC' : 'Enter Maker TAC') : 'Enter TAC';

    const lines: string[] = [];
    if (username) lines.push(`Username: <strong>${username}</strong>`);
    if (challengeCode) lines.push(`Challenge Code: <strong>${challengeCode}</strong>`);
    const html = lines.length ? lines.join('<br>') : undefined;

    Swal.fire({
      title,
      html,
      input: 'text',
      showCancelButton: true,
      confirmButtonText: 'Submit',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        const data = {
          bank_account_id: row.bank_account_id,
          tac: result.value,
          requester_role: requesterRole,
          action: action
        };
        this.botStatusDataService.updateTAC(data).subscribe(() =>
          this.messages$ = this.botStatusDataService.messages$
        );
      }
    });
  }

  onClearDate() {
    if (this.form.value.start_date !== null) {
      this.form.patchValue({ defaultDate: null });
    }
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_date: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_date: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  isValidDate(date: Date | string) {
    if (moment(date).isValid()) {
      return true;
    }
    return false;
  }

  getHostnameById(id: number): string {
    const server = this.dropdown.servers.find(s => s.id === id);
    return server ? server.hostname : 'No Server';
  }

  onOpenDialog(type: string, row?: any) {
    if (type === 'reconciliation-job') {
      this.openDialogBy(ReconciliationJobCreateComponent);
    } else if (type === 'transaction-list') {
      const botStatusData = this.botStatusDataService.getBankTransactionList(row.bank_account_id);
      this.subscription = botStatusData.pipe(
        tap((res: any) => {
          if (res) {
            this.openDialogBy(BotTransactionListComponent, res);
          }
        })
      ).subscribe();
    }
  }

  private openDialogBy(componentRef: any, data?: any) {
    const dialogRef = this.dialog.open(componentRef, {
      width: '1500px',
      height: 'auto',
      data: {
        transaction_data: data,
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.reload();
      }
    });
  }

  getStatues(row: any) {
    switch(row.mode) {
      case 1: // Deposit
        return this.statuses;
      
      case 2: // Withdrawal
        return [
          "Disabled",
          "Enabled (Auto)"
        ];
    }
  }
}
